import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { ApiPageResponse, PageQuery } from '../types/api.types';
import { Invoice, MarkInvoicePaidRequest } from '../types/invoice.types';

@Injectable({ providedIn: 'root' })
export class InvoicesService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.gatewayUrl}/rental/api/invoices`;

  list(query: PageQuery = {}): Observable<ApiPageResponse<Invoice>> {
    return this.http.get<ApiPageResponse<Invoice>>(this.base, {
      params: this.pageParams(query),
    });
  }

  get(id: number): Observable<Invoice> {
    return this.http.get<Invoice>(`${this.base}/${id}`);
  }

  getByRental(rentalId: number): Observable<Invoice> {
    return this.http.get<Invoice>(`${this.base}/rental/${rentalId}`);
  }

  /** Admin/Owner action — marks an invoice as paid, returns the updated entity. */
  markAsPaid(id: number, payload: MarkInvoicePaidRequest): Observable<Invoice> {
    let params = new HttpParams().set('paymentMethod', payload.paymentMethod);
    if (payload.paymentReference?.trim()) {
      params = params.set('paymentReference', payload.paymentReference.trim());
    }
    return this.http.post<Invoice>(`${this.base}/${id}/mark-paid`, null, { params });
  }

  cancel(id: number, reason?: string): Observable<Invoice> {
    let params = new HttpParams();
    if (reason?.trim()) params = params.set('reason', reason.trim());
    return this.http.post<Invoice>(`${this.base}/${id}/cancel`, null, { params });
  }

  /** Batch — refresh OVERDUE status on all past-due invoices. */
  updateOverdue(): Observable<string> {
    return this.http.post(`${this.base}/update-overdue`, null, {
      responseType: 'text',
    });
  }

  count(search?: string): Observable<number> {
    let params = new HttpParams();
    if (search?.trim()) params = params.set('search', search.trim());
    return this.http.get<number>(`${this.base}/count`, { params });
  }

  private pageParams(query: PageQuery): HttpParams {
    let params = new HttpParams()
      .set('pageable', 'true')
      .set('page', String(query.page ?? 1))
      .set('size', String(query.size ?? 10))
      .set('isDeleted', String(query.isDeleted ?? false));
    if (query.search?.trim()) params = params.set('search', query.search.trim());
    return params;
  }
}
